/**
 * IndexedDB 工具类，用于管理物流会话存储
 */
class LogisticsConversationStorage {
  constructor() {
    this.dbName = 'AI_Merchant_Logistics_DB';
    this.dbVersion = 2; // 升级版本以添加用户ID索引
    this.storeName = 'logistics_conversations';
    this.db = null;
  }

  /**
   * 初始化数据库
   * @returns {Promise<IDBDatabase>} 数据库实例
   */
  async initDB() {
    return new Promise((resolve, reject) => {
      if (this.db) {
        resolve(this.db);
        return;
      }

      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = (event) => {
        console.error('IndexedDB open error:', event.target.error);
        reject(event.target.error);
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // 创建物流会话存储对象
        if (!db.objectStoreNames.contains(this.storeName)) {
          const objectStore = db.createObjectStore(this.storeName, { keyPath: 'id' });
          // 创建索引以支持按时间排序
          objectStore.createIndex('updatedAt', 'updatedAt', { unique: false });
          // 创建索引以支持按用户ID过滤
          objectStore.createIndex('userId', 'userId', { unique: false });
        } else {
          // 如果对象存储已存在，检查是否需要添加userId索引
          const transaction = event.target.transaction;
          const objectStore = transaction.objectStore(this.storeName);
          if (!objectStore.indexNames.contains('userId')) {
            objectStore.createIndex('userId', 'userId', { unique: false });
          }
        }
      };
    });
  }

  /**
   * 获取所有物流会话
   * @param {string} [userId] 用户ID，可选
   * @returns {Promise<Array>} 会话列表
   */
  async getAllConversations(userId) {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readonly');
        const objectStore = transaction.objectStore(this.storeName);
        let request;

        if (userId) {
          // 使用userId索引获取特定用户的会话
          const index = objectStore.index('userId');
          request = index.getAll(userId);
        } else {
          // 获取所有会话（兼容旧版本）
          request = objectStore.getAll();
        }

        request.onerror = (event) => {
          console.error('Failed to get logistics conversations:', event.target.error);
          reject(event.target.error);
        };

        request.onsuccess = (event) => {
          const conversations = event.target.result || [];
          // 按更新时间降序排序
          conversations.sort((a, b) => b.updatedAt - a.updatedAt);
          resolve(conversations);
        };
      });
    } catch (error) {
      console.error('Error getting logistics conversations:', error);
      return [];
    }
  }

  /**
   * 保存单个物流会话
   * @param {Object} conversation 会话对象
   * @param {string} userId 用户ID
   * @returns {Promise<boolean>} 是否保存成功
   */
  async saveConversation(conversation, userId) {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        // 先检查会话是否已存在
        const transaction = db.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);
        const getRequest = objectStore.get(conversation.id);

        getRequest.onerror = (event) => {
          console.error('Failed to check existing logistics conversation:', event.target.error);
          reject(event.target.error);
        };

        getRequest.onsuccess = (event) => {
          const existingConversation = event.target.result;
          let conversationToSave;

          if (existingConversation) {
            // 会话已存在，保留原有的userID，只更新其他字段
            conversationToSave = {
              ...existingConversation,
              title: conversation.title,
              preview: conversation.preview,
              messages: conversation.messages,
              updatedAt: conversation.updatedAt
            };
          } else {
            // 会话不存在，添加新会话并设置userID
            conversationToSave = { ...conversation, userId };
          }

          // 保存会话
          const putRequest = objectStore.put(conversationToSave);

          putRequest.onerror = (event) => {
            console.error('Failed to save logistics conversation:', event.target.error);
            reject(event.target.error);
          };

          putRequest.onsuccess = () => {
            resolve(true);
          };
        };
      });
    } catch (error) {
      console.error('Error saving logistics conversation:', error);
      return false;
    }
  }

  /**
   * 保存多个物流会话
   * @param {Array} conversations 会话数组
   * @param {string} userId 用户ID
   * @returns {Promise<boolean>} 是否保存成功
   */
  async saveConversations(conversations, userId) {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);
        
        let completed = 0;
        let hasError = false;
        
        // 遍历会话列表，保存每个会话
        conversations.forEach(conversation => {
          // 先检查会话是否已存在
          const getRequest = objectStore.get(conversation.id);
          
          getRequest.onerror = (event) => {
            console.error('Failed to check existing logistics conversation:', event.target.error);
            hasError = true;
            completed++;
            if (completed === conversations.length) {
              resolve(!hasError);
            }
          };
          
          getRequest.onsuccess = (event) => {
            const existingConversation = event.target.result;
            let conversationToSave;
            
            if (existingConversation) {
              // 会话已存在，保留原有的userID和其他字段，只更新必要的字段
              conversationToSave = {
                ...existingConversation,
                title: conversation.title || existingConversation.title,
                preview: conversation.preview || existingConversation.preview,
                messages: conversation.messages || existingConversation.messages,
                updatedAt: conversation.updatedAt || existingConversation.updatedAt
              };
            } else {
              // 会话不存在，添加新会话并设置userID
              conversationToSave = { ...conversation, userId };
            }
            
            // 保存会话
            const putRequest = objectStore.put(conversationToSave);
            
            putRequest.onerror = (event) => {
              console.error('Failed to save logistics conversation:', event.target.error);
              hasError = true;
            };
            
            putRequest.onsuccess = () => {
              completed++;
              if (completed === conversations.length) {
                resolve(!hasError);
              }
            };
          };
        });
      });
    } catch (error) {
      console.error('Error saving logistics conversations:', error);
      return false;
    }
  }

  /**
   * 获取单个物流会话
   * @param {string} conversationId 会话ID
   * @param {string} [userId] 用户ID，可选，用于验证会话归属
   * @returns {Promise<Object|null>} 会话对象或null
   */
  async getConversation(conversationId, userId) {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readonly');
        const objectStore = transaction.objectStore(this.storeName);
        const request = objectStore.get(conversationId);

        request.onerror = (event) => {
          console.error('Failed to get logistics conversation:', event.target.error);
          reject(event.target.error);
        };

        request.onsuccess = (event) => {
          const conversation = event.target.result;
          
          if (conversation) {
            // 如果提供了用户ID，验证会话归属
            if (userId && conversation.userId !== userId) {
              console.warn('Cannot access logistics conversation that does not belong to the specified user');
              resolve(null);
            } else {
              resolve(conversation);
            }
          } else {
            resolve(null);
          }
        };
      });
    } catch (error) {
      console.error('Error getting logistics conversation:', error);
      return null;
    }
  }

  /**
   * 删除物流会话
   * @param {string} conversationId 会话ID
   * @param {string} [userId] 用户ID，可选，用于验证会话归属
   * @returns {Promise<boolean>} 是否删除成功
   */
  async deleteConversation(conversationId, userId) {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        // 使用读写事务进行删除操作
        const transaction = db.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);

        // 先获取会话以验证归属（如果提供了用户ID）
        if (userId) {
          const getRequest = objectStore.get(conversationId);

          getRequest.onerror = (event) => {
            console.error('Failed to get logistics conversation for deletion:', event.target.error);
            reject(event.target.error);
          };

          getRequest.onsuccess = (event) => {
            const conversation = event.target.result;
            
            if (!conversation) {
              // 会话不存在
              console.warn('Logistics conversation not found for deletion:', conversationId);
              resolve(false);
            } else if (conversation.userId !== userId) {
              // 会话不属于该用户
              console.warn('Cannot delete logistics conversation that does not belong to the specified user');
              resolve(false);
            } else {
              // 会话属于该用户，可以删除
              const deleteRequest = objectStore.delete(conversationId);

              deleteRequest.onerror = (event) => {
                console.error('Failed to delete logistics conversation:', event.target.error);
                reject(event.target.error);
              };

              deleteRequest.onsuccess = () => {
                resolve(true);
              };
            }
          };
        } else {
          // 没有提供用户ID，直接删除
          const deleteRequest = objectStore.delete(conversationId);

          deleteRequest.onerror = (event) => {
            console.error('Failed to delete logistics conversation:', event.target.error);
            reject(event.target.error);
          };

          deleteRequest.onsuccess = () => {
            resolve(true);
          };
        }
      });
    } catch (error) {
      console.error('Error deleting logistics conversation:', error);
      return false;
    }
  }

  /**
   * 清空所有物流会话
   * @param {string} [userId] 用户ID，可选，用于清空特定用户的会话
   * @returns {Promise<boolean>} 是否清空成功
   */
  async clearAllConversations(userId) {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);
        
        if (userId) {
          // 清空特定用户的会话
          console.log('Clearing logistics conversations for user:', userId);
          
          // 使用userId索引获取特定用户的所有会话
          const index = objectStore.index('userId');
          const request = index.getAll(userId);
          
          request.onerror = (event) => {
            console.error('Failed to get logistics conversations for clearing:', event.target.error);
            reject(event.target.error);
          };
          
          request.onsuccess = (event) => {
            const conversations = event.target.result || [];
            let completed = 0;
            let hasError = false;
            
            if (conversations.length === 0) {
              // 没有会话需要清空
              resolve(true);
              return;
            }
            
            // 逐个删除会话
            conversations.forEach(conversation => {
              const deleteRequest = objectStore.delete(conversation.id);
              
              deleteRequest.onerror = (event) => {
                console.error('Failed to delete logistics conversation during clear:', event.target.error);
                hasError = true;
              };
              
              deleteRequest.onsuccess = () => {
                completed++;
                if (completed === conversations.length) {
                  resolve(!hasError);
                }
              };
            });
          };
        } else {
          // 清空所有会话（兼容旧版本）
          console.log('Clearing all logistics conversations');
          const request = objectStore.clear();

          request.onerror = (event) => {
            console.error('Failed to clear logistics conversations:', event.target.error);
            reject(event.target.error);
          };

          request.onsuccess = () => {
            resolve(true);
          };
        }
      });
    } catch (error) {
      console.error('Error clearing logistics conversations:', error);
      return false;
    }
  }
}

// 创建单例实例
const logisticsConversationStorage = new LogisticsConversationStorage();

export default logisticsConversationStorage;
